// src/pages/platform/PlatformAuditPage.tsx
// Platform audit log — activity history with filters.

import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type BadgeVariant = 'success' | 'warning' | 'error' | 'default' | 'secondary';

function actionVariant(action?: string): BadgeVariant {
  if (!action) return 'secondary';
  if (action.includes('CREATED') || action.includes('ADDED') || action.includes('GRANTED') || action.includes('ACCEPTED')) return 'success';
  if (action.includes('UPDATED') || action.includes('CHANGED')) return 'default';
  if (action.includes('DELETED') || action.includes('REMOVED') || action.includes('REVOKED') || action.includes('SUSPENDED')) return 'error';
  return 'secondary';
}

function formatAction(action?: string): string {
  if (!action) return 'Unknown';
  return action
    .replace(/^PLATFORM_AUDIT_ACTION_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatDate(d?: string): string {
  if (!d) return '--';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}

/** Try to format a value as indented JSON; fall back to raw string. */
function formatJson(value?: string): string {
  if (!value) return '';
  try {
    const parsed = JSON.parse(value);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
}

// ---------------------------------------------------------------------------
// Action type options
// ---------------------------------------------------------------------------

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'PLATFORM_AUDIT_ACTION_CREATED', label: 'Platform Created' },
  { value: 'PLATFORM_AUDIT_ACTION_UPDATED', label: 'Platform Updated' },
  { value: 'PLATFORM_AUDIT_ACTION_SUSPENDED', label: 'Platform Suspended' },
  { value: 'PLATFORM_AUDIT_ACTION_ACTIVATED', label: 'Platform Activated' },
  { value: 'PLATFORM_AUDIT_ACTION_DELETED', label: 'Platform Deleted' },
  { value: 'PLATFORM_AUDIT_ACTION_MEMBER_ADDED', label: 'Member Added' },
  { value: 'PLATFORM_AUDIT_ACTION_MEMBER_REMOVED', label: 'Member Removed' },
  { value: 'PLATFORM_AUDIT_ACTION_MEMBER_ROLE_CHANGED', label: 'Role Changed' },
  { value: 'PLATFORM_AUDIT_ACTION_PRODUCT_ENABLED', label: 'Product Enabled' },
  { value: 'PLATFORM_AUDIT_ACTION_PRODUCT_DISABLED', label: 'Product Disabled' },
  { value: 'PLATFORM_AUDIT_ACTION_FEDERATION_PEER_CREATED', label: 'Federation Peer Created' },
  { value: 'PLATFORM_AUDIT_ACTION_FEDERATION_PEER_REVOKED', label: 'Federation Peer Revoked' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlatformAuditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = usePlatform();
  const { t } = useI18n();

  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Audit log data. Currently no store method exists, so we show a placeholder.
  // When ListPlatformAuditLogs is available, replace this with a store call.
  const [auditEntries] = useState<any[]>([]);

  useEffect(() => {
    if (id) store.GetPlatform(id);
  }, [id]);

  const platformName = (store.currentItem as any)?.name ?? '';

  const filteredEntries = auditEntries.filter((entry) => {
    if (actionFilter && entry.action !== actionFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      const matches =
        entry.actor?.display_name?.toLowerCase().includes(s) ||
        entry.action?.toLowerCase().includes(s) ||
        entry.target?.id?.toLowerCase().includes(s) ||
        entry.ip_address?.includes(s);
      if (!matches) return false;
    }
    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      const ts = new Date(entry.occurred_at ?? entry.created_at).getTime();
      if (ts < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo).getTime() + 86_400_000; // end of day
      const ts = new Date(entry.occurred_at ?? entry.created_at).getTime();
      if (ts > to) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
            {t('platform.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/platforms/${id}`} className="text-muted-foreground hover:text-foreground">
            {platformName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('platform.audit.title')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('platform.audit.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('platform.audit.subtitle')}
        </p>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Filter bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} fullWidth={false}>
              {ACTION_TYPES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </Select>
            <div className="flex gap-2 items-center">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth={false}
              />
              <span className="text-sm text-muted-foreground">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth={false}
              />
            </div>
            <Input
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth={false}
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Placeholder notice (shown when no audit API is connected) */}
      {auditEntries.length === 0 && (
        <Alert variant="info">
          {t('platform.audit.placeholder')}
        </Alert>
      )}

      {/* Table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('platform.audit.columnAction')}</TableHead>
              <TableHead>{t('platform.audit.columnActor')}</TableHead>
              <TableHead>{t('platform.audit.columnTarget')}</TableHead>
              <TableHead>{t('platform.audit.columnIp')}</TableHead>
              <TableHead>{t('platform.audit.columnTimestamp')}</TableHead>
              <TableHead className="text-right">{t('platform.audit.columnDetails')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEntries.length === 0 ? (
              <TableEmpty colSpan={6} message={t('platform.audit.noEntries')} />
            ) : (
              filteredEntries.map((entry: any) => {
                const entryId = entry.entity?.id ?? entry.id ?? String(Math.random());
                const hasDetails = !!(entry.old_value || entry.new_value);
                const isExpanded = expandedRow === entryId;

                return (
                  <Fragment key={entryId}>
                    <TableRow>
                      <TableCell>
                        <Badge variant={actionVariant(entry.action)}>
                          {formatAction(entry.action)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-foreground">
                          {entry.actor?.display_name || entry.actor?.entity?.id || '--'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono text-foreground">
                          {entry.target?.id || entry.target_urn || '--'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono text-muted-foreground">
                          {entry.ip_address || '--'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(entry.occurred_at ?? entry.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {hasDetails ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedRow(isExpanded ? null : entryId)}
                          >
                            {isExpanded
                              ? t('common.hide')
                              : t('common.show')}
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">--</span>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-2">
                            {entry.old_value && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Old Value</p>
                                <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap">
                                  {formatJson(entry.old_value)}
                                </pre>
                              </div>
                            )}
                            {entry.new_value && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">New Value</p>
                                <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap">
                                  {formatJson(entry.new_value)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default PlatformAuditPage;
